import type { MessageDescriptor } from 'react-intl';

import { ApiErrorMessage, IntlApiErrorMessages } from './ApiErrorMessages.intl';
import { IntlNoticeMessages, NoticeMessage } from './NoticeMessages.intl';

export enum NoticeType {
	ConfirmDeleteForm = 'ConfirmDeleteForm',
	ConfirmChangeFormToInternal = 'ConfirmChangeFormToInternal',
	ConfirmChangeFormToExternal = 'ConfirmChangeFormToExternal',
	ConfirmCancelEdit = 'ConfirmCancelEdit',
	ConfirmSubmitForm = 'ConfirmSubmitForm',
	ConfirmSubmitFormLock = 'ConfirmSubmitFormLock',
	ConfirmReopenForm = 'ConfirmReopenForm',
	ErrorApiUnauthorised = 'ErrorApiUnauthorised',
	ErrorApiMissingPermissions = 'ErrorApiMissingPermissions',
	ErrorApiAddConnectionFailed = 'ErrorApiAddConnectionFailed',
	ErrorApiUpdateConnectionFailed = 'ErrorApiUpdateConnectionFailed',
	ErrorApiSubmitFormFailed = 'ErrorApiSubmitFormFailed',
	ErrorApiSaveFormFailed = 'ErrorApiSaveFormFailed',
	ErrorApiSaveFormFailedOnConflict = 'ErrorApiSaveFormFailedOnConflict',
	ErrorApiSaveFormInvalidAnswersFailed = 'ErrorApiSaveFormInvalidAnswersFailed',
	ErrorApiSaveTemplateFormFailed = 'ErrorApiSaveTemplateFormFailed',
	ErrorApiLoadTemplateFormFailed = 'ErrorApiLoadTemplateFormFailed',
	ErrorApiLoadJiraFieldsFailed = 'ErrorApiLoadJiraFieldsFailed',
	ErrorApiLoadDataConnectionsFailed = 'ErrorApiLoadDataConnectionsFailed',
	ErrorApiLoadSettingsRefDataFailed = 'ErrorApiLoadSettingsRefDataFailed',
}

interface NoticeConfig {
	title: MessageDescriptor;
	message: MessageDescriptor;
	confirmText: MessageDescriptor;
	isErrorNotice?: boolean;
	isWarningNotice?: boolean;
	confirmLink?: string;
}

export const Notices: Record<NoticeType, NoticeConfig> = {
	[NoticeType.ConfirmDeleteForm]: {
		title: IntlNoticeMessages[NoticeMessage.DeleteFormTitle],
		message: IntlNoticeMessages[NoticeMessage.DeleteFormMsg],
		confirmText: IntlNoticeMessages[NoticeMessage.Delete],
		isWarningNotice: true,
	},
	[NoticeType.ConfirmChangeFormToInternal]: {
		title: IntlNoticeMessages[NoticeMessage.ChangeFormToInternalHeader],
		message: IntlNoticeMessages[NoticeMessage.ChangeFormToInternalMsg],
		confirmText: IntlNoticeMessages[NoticeMessage.MakeInternal],
	},
	[NoticeType.ConfirmChangeFormToExternal]: {
		title: IntlNoticeMessages[NoticeMessage.ChangeFormToExternalHeader],
		message: IntlNoticeMessages[NoticeMessage.ChangeFormToExternalMsg],
		confirmText: IntlNoticeMessages[NoticeMessage.MakeExternal],
	},
	[NoticeType.ConfirmCancelEdit]: {
		title: IntlNoticeMessages[NoticeMessage.CancelEditFormTitle],
		message: IntlNoticeMessages[NoticeMessage.CancelEditFormMsg],
		confirmText: IntlNoticeMessages[NoticeMessage.Confirm],
	},
	[NoticeType.ConfirmSubmitForm]: {
		title: IntlNoticeMessages[NoticeMessage.SubmitFormTitle],
		message: IntlNoticeMessages[NoticeMessage.SubmitFormMsg],
		confirmText: IntlNoticeMessages[NoticeMessage.Submit],
	},
	[NoticeType.ConfirmSubmitFormLock]: {
		title: IntlNoticeMessages[NoticeMessage.SubmitFormTitle],
		message: IntlNoticeMessages[NoticeMessage.SubmitFormLockMsg],
		confirmText: IntlNoticeMessages[NoticeMessage.Submit],
	},
	[NoticeType.ConfirmReopenForm]: {
		title: IntlNoticeMessages[NoticeMessage.ReopenFormTitle],
		message: IntlNoticeMessages[NoticeMessage.ReopenFormMsg],
		confirmText: IntlNoticeMessages[NoticeMessage.Reopen],
	},
	[NoticeType.ErrorApiUnauthorised]: {
		title: IntlApiErrorMessages[ApiErrorMessage.UnauthorisedTitle],
		message: IntlApiErrorMessages[ApiErrorMessage.UnauthorisedMsg],
		confirmText: IntlNoticeMessages[NoticeMessage.Close],
		isErrorNotice: true,
	},
	[NoticeType.ErrorApiMissingPermissions]: {
		title: IntlApiErrorMessages[ApiErrorMessage.MissingPermissionsTitle],
		message: IntlApiErrorMessages[ApiErrorMessage.MissingPermissionsMsg],
		confirmText: IntlNoticeMessages[NoticeMessage.Close],
		isErrorNotice: true,
	},
	[NoticeType.ErrorApiAddConnectionFailed]: {
		title: IntlApiErrorMessages[ApiErrorMessage.AddConnectionFailedTitle],
		message: IntlApiErrorMessages[ApiErrorMessage.AddConnectionFailedMsg],
		confirmText: IntlNoticeMessages[NoticeMessage.Close],
		isErrorNotice: true,
	},
	[NoticeType.ErrorApiUpdateConnectionFailed]: {
		title: IntlApiErrorMessages[ApiErrorMessage.UpdateConnectionFailedTitle],
		message: IntlApiErrorMessages[ApiErrorMessage.UpdateConnectionFailedMsg],
		confirmText: IntlNoticeMessages[NoticeMessage.Close],
		isErrorNotice: true,
	},
	[NoticeType.ErrorApiSubmitFormFailed]: {
		title: IntlApiErrorMessages[ApiErrorMessage.SubmitFormFailedTitle],
		message: IntlApiErrorMessages[ApiErrorMessage.SubmitFormFailedMsg],
		confirmText: IntlNoticeMessages[NoticeMessage.Close],
		isErrorNotice: true,
	},
	[NoticeType.ErrorApiSaveFormFailed]: {
		title: IntlApiErrorMessages[ApiErrorMessage.SaveFormFailedTitle],
		message: IntlApiErrorMessages[ApiErrorMessage.SaveFormFailedMsg],
		confirmText: IntlNoticeMessages[NoticeMessage.Close],
		isErrorNotice: true,
	},
	[NoticeType.ErrorApiSaveFormInvalidAnswersFailed]: {
		title: IntlApiErrorMessages[ApiErrorMessage.SaveFormFailedTitle],
		message: IntlApiErrorMessages[ApiErrorMessage.SaveFormInvalidAnswersMsg],
		confirmText: IntlNoticeMessages[NoticeMessage.Close],
		isErrorNotice: true,
	},
	[NoticeType.ErrorApiSaveFormFailedOnConflict]: {
		title: IntlApiErrorMessages[ApiErrorMessage.SaveFormFailedTitle],
		message: IntlApiErrorMessages[ApiErrorMessage.SaveFormFailedOnConflictMsg],
		confirmText: IntlNoticeMessages[NoticeMessage.Close],
		isErrorNotice: true,
	},
	[NoticeType.ErrorApiSaveTemplateFormFailed]: {
		title: IntlApiErrorMessages[ApiErrorMessage.SaveTemplateFormFailedTitle],
		message: IntlApiErrorMessages[ApiErrorMessage.SaveTemplateFormFailedMsg],
		confirmText: IntlNoticeMessages[NoticeMessage.Close],
		isErrorNotice: true,
	},
	[NoticeType.ErrorApiLoadTemplateFormFailed]: {
		title: IntlApiErrorMessages[ApiErrorMessage.LoadTemplateFormFailedTitle],
		message: IntlApiErrorMessages[ApiErrorMessage.LoadTemplateFormFailedMsg],
		confirmText: IntlNoticeMessages[NoticeMessage.Close],
		isErrorNotice: true,
	},
	[NoticeType.ErrorApiLoadJiraFieldsFailed]: {
		title: IntlApiErrorMessages[ApiErrorMessage.LoadJiraFieldsFailedTitle],
		message: IntlApiErrorMessages[ApiErrorMessage.LoadJiraFieldsFailedMsg],
		confirmText: IntlNoticeMessages[NoticeMessage.Close],
		isErrorNotice: true,
	},
	[NoticeType.ErrorApiLoadDataConnectionsFailed]: {
		title: IntlApiErrorMessages[ApiErrorMessage.LoadDataConnectionsFailedTitle],
		message: IntlApiErrorMessages[ApiErrorMessage.LoadDataConnectionsFailedMsg],
		confirmText: IntlNoticeMessages[NoticeMessage.Close],
		isErrorNotice: true,
	},
	[NoticeType.ErrorApiLoadSettingsRefDataFailed]: {
		title: IntlApiErrorMessages[ApiErrorMessage.LoadSettingsRefDataFailedTitle],
		message: IntlApiErrorMessages[ApiErrorMessage.LoadSettingsRefDataFailedMsg],
		confirmText: IntlNoticeMessages[NoticeMessage.Close],
		isErrorNotice: true,
	},
};
